import React, { useState, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { useTryOn } from '../contexts/TryOnContext';
import { subscribeTryOnSession } from '../utils/firestoreUtils';
import '../styles/hologram.css';
import './VideoGallery.css';

/**
 * VideoGallery - Full-screen holographic video player with navigation
 * Shows 360° rotation videos generated by Veo 3.1
 */
function VideoGallery() {
  const { tryOnResults, sessionId, retrievedSession, requestReset, setTryOnResults } = useTryOn();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Determine which session to use (retrieved session takes precedence)
  const activeSessionId = retrievedSession?.sessionId || sessionId;

  // If we have a retrieved session, initialize results from it
  useEffect(() => {
    if (retrievedSession?.results) {
      console.log('VideoGallery: Loading results from retrieved session');
      setTryOnResults(retrievedSession.results);
    }
  }, [retrievedSession, setTryOnResults]);

  // Subscribe to Firestore to get real-time video updates
  useEffect(() => {
    if (!activeSessionId) return;

    console.log('VideoGallery: Subscribing to Firestore for video updates...');
    console.log('VideoGallery: Active Session ID:', activeSessionId);

    const unsubscribe = subscribeTryOnSession(activeSessionId, (data, err) => {
      if (err || !data) {
        console.error('VideoGallery: Firestore error', err);
        return;
      }

      console.log('🔔 VideoGallery: Firestore update received at', new Date().toLocaleTimeString());
      console.log('VideoGallery: Session status:', data.status);

      const results = Array.isArray(data.results) ? data.results : [];
      console.log('VideoGallery: Results count:', results.length);

      results.forEach((r, i) => {
        console.log(`  Result ${i} (${r?.productName}):`, {
          status: r?.status,
          hasVideo: !!r?.videoPublicUrl,
          hasTryOn: !!r?.tryOnImagePublicUrl,
          videoUrl: r?.videoPublicUrl
        });
      });

      // Update context with latest results (ensure it's an array)
      if (Array.isArray(data.results)) {
        console.log('VideoGallery: Updating tryOnResults in context with', data.results.length, 'results');
        setTryOnResults(data.results);
      } else {
        console.error('VideoGallery: data.results is not an array:', typeof data.results, data.results);
      }
    });

    // Also poll manually every 5 seconds as fallback
    const pollInterval = setInterval(async () => {
      console.log('VideoGallery: Manual poll check...');
      try {
        const { getTryOnSession } = await import('../utils/firestoreUtils');
        const freshData = await getTryOnSession(activeSessionId);
        console.log('VideoGallery: Manual poll - hasVideo:', !!freshData.results?.[0]?.videoPublicUrl);
        if (freshData.results && Array.isArray(freshData.results)) {
          setTryOnResults(freshData.results);
        }
      } catch (pollErr) {
        console.error('VideoGallery: Poll error:', pollErr);
      }
    }, 5000);

    return () => {
      console.log('VideoGallery: Unsubscribing from Firestore');
      unsubscribe();
      clearInterval(pollInterval);
    };
  }, [activeSessionId, setTryOnResults]);

  // Ensure tryOnResults is an array before filtering
  const resultsArray = Array.isArray(tryOnResults) ? tryOnResults : [];

  // Filter completed results (must have at least try-on image)
  const completedResults = resultsArray.filter(r =>
    r && r.tryOnImagePublicUrl // Show if we have try-on image (video optional)
  );

  const currentResult = completedResults[currentIndex] || null;

  console.log('VideoGallery state:', {
    tryOnResultsType: typeof tryOnResults,
    isArray: Array.isArray(tryOnResults),
    resultsArrayLength: resultsArray.length,
    completedCount: completedResults.length,
    currentIndex,
    currentResultName: currentResult?.productName
  });

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, completedResults.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < completedResults.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleStartOver = () => {
    requestReset();
  };

  if (!completedResults || completedResults.length === 0) {
    return (
      <div className="video-gallery-container">
        <div className="error-message-holo holo-card">
          <h2>No holographic videos available</h2>
          <p>Videos are still being generated or failed to process</p>
          <button className="holo-button" onClick={handleStartOver}>
            Start Over
          </button>
        </div>
      </div>
    );
  }

  if (!currentResult) {
    console.error('Current result is undefined. Index:', currentIndex, 'Results:', completedResults);
    return (
      <div className="video-gallery-container">
        <div className="error-message-holo holo-card">
          <h2>Loading...</h2>
          <p>Please wait</p>
        </div>
      </div>
    );
  }

  const qrUrl = `${window.location.origin}/qr/${activeSessionId}`;
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < completedResults.length - 1;

  console.log('VideoGallery render:', {
    currentIndex,
    totalResults: completedResults.length,
    currentResult: currentResult?.productName,
    hasVideo: !!currentResult?.videoPublicUrl
  });

  return (
    <div className="video-gallery-container">
      {/* Try-on photos (all loaded, only current visible) */}
      {completedResults.map((result, index) => (
        <div
          key={result.productId}
          className={`video-wrapper ${index === currentIndex ? 'active' : 'inactive'}`}
        >
          <img
            src={result.tryOnImagePublicUrl}
            alt={result.productName}
            className="hologram-video"
            onError={() => console.error('VideoGallery image failed to load:', result.productName, result.tryOnImagePublicUrl)}
          />
        </div>
      ))}

      {/* Product info overlay */}
      {currentResult && (
        <div className="product-info-overlay holo-card">
          <h3 className="product-name-overlay">{currentResult.productName}</h3>
          <div className="product-meta">
            <span className="product-size-overlay">Size: {currentResult.size}</span>
            <span className="product-price-overlay">{currentResult.price}</span>
          </div>
        </div>
      )}

      {/* Navigation arrows */}
      {canGoPrev && (
        <button
          className="video-nav-arrow video-nav-left holo-button-secondary"
          onClick={handlePrevious}
        >
          ‹
        </button>
      )}

      {canGoNext && (
        <button
          className="video-nav-arrow video-nav-right holo-button-secondary"
          onClick={handleNext}
        >
          ›
        </button>
      )}

      {/* Page indicator dots */}
      <div className="video-page-indicator">
        {completedResults.map((_, index) => (
          <button
            key={index}
            className={`dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </div>

      {/* QR Code - bottom right */}
      <div className="qr-floating-card holo-card">
        <QRCodeCanvas
          value={qrUrl}
          size={260}
          level="H"
          className="qr-code"
        />
      </div>

      {/* Start Over - bottom left */}
      <div className="start-over-floating-card">
        <button className="holo-button-secondary" onClick={handleStartOver}>
          Start Over
        </button>
      </div>
    </div>
  );
}

export default VideoGallery;
